import "./calculator.sass";
import { useMemo, useState } from "react";
import DonateLink from "../shared/donate-link";
import { Typography } from "@mui/material";
import CalculatorInputForm from "../calculator-input-form/calculator-input-form";
import CalculatorTable from "../calculator-table/calculator-table";
import { NOT_AVAILABLE } from "../shared/constants";

export interface CalculatorRow {
  year: number;
  fiscalYear: number;
  fcf: number;
  pvFcf: number;
}

interface FormValue {
  ticker: string;
  discountRate: number;
  freeCashFlow: number;
  cash: number;
  exitMultiple: number;
  fcfGrowthOne: number;
  fcfGrowthTwo: number;
  fcfGrowthThree: number;
  shares: number;
  marketCap: number;
}

function createData(
  year: number,
  fiscalYear: number,
  fcf: number,
  pvFcf: number
): CalculatorRow {
  return { year, fiscalYear, fcf, pvFcf };
}

const presentValue = (cf: number, i: number, discountRate: number) => {
  return Math.round(cf / (1 + discountRate / 100) ** i || 0);
};

const fiscalYear = (num: number) => {
  return new Date().getFullYear() + num;
};

const getGrowthRate = (index: number, form: FormValue) => {
  let growthRate = 0;
  if ([1, 2, 3].includes(index)) {
    growthRate = form.fcfGrowthOne;
  } else if ([4, 5, 6].includes(index)) {
    growthRate = form.fcfGrowthTwo;
  } else if ([7, 8, 9, 10].includes(index)) {
    growthRate = form.fcfGrowthThree;
  }
  return growthRate;
};

function fcfValue(fcf: number, index: number, form: FormValue): number {
  const growthRate = getGrowthRate(index, form);
  return fcf * (1 + growthRate / 100) || 0;
}

const defaultValues = {
  ticker: "AAPL",
  discountRate: 15,
  freeCashFlow: 0,
  cash: 0,
  exitMultiple: 15,
  fcfGrowthOne: 20,
  fcfGrowthTwo: 15,
  fcfGrowthThree: 10,
  shares: 0,
  marketCap: 0,
};

const totalYears = Array.from(Array(11).keys());

export default function Calculator() {
  const [form, setFormValues] = useState(defaultValues);

  const [rows, setRows] = useState([
    {
      year: 0,
      fiscalYear: fiscalYear(0),
      fcf: defaultValues.freeCashFlow,
      pvFcf: presentValue(
        defaultValues.freeCashFlow,
        0,
        defaultValues.discountRate
      ),
    },
  ]);

  const [cashFlows, setCashFlows] = useState([0]);
  const [presentValueCashFlows, setPresentValueCashFlows] = useState([0]);

  const handleInputChange = (stateValues: {
    name: string;
    value: string | number;
  }) => {
    const { name, value } = stateValues;

    setFormValues({
      ...form,
      [name]: value,
    });
  };

  // calculateCashFlows
  useMemo(() => {
    const cashFlows: number[] = [];
    totalYears.forEach((num) => {
      const cf = num === 0 ? form.freeCashFlow : cashFlows[num - 1];
      cashFlows.push(fcfValue(cf, num, form));
    });
    setCashFlows(cashFlows);
  }, [form]);

  // calculatePresentValueCashFlows
  useMemo(() => {
    const presentValueCashFlows = cashFlows.map((cf: number, i: number) => {
      const fcf =
        i === 0 ? form.freeCashFlow : presentValue(cf, i, form.discountRate);
      return fcf;
    });
    setPresentValueCashFlows(presentValueCashFlows);
  }, [cashFlows, form]);

  const calculateValuePerShare = () => {
    const sharesValue = form.marketCap / form.shares;
    return Number.isFinite(sharesValue) ? sharesValue : NOT_AVAILABLE;
  };

  const calculateIntrinsicValuePerShare = () => {
    const sharesValue = presentValueSum(true) / form.shares;
    return Number.isFinite(sharesValue) ? sharesValue : NOT_AVAILABLE;
  };

  const calculateDiscount = () => {
    return (1 - form.marketCap / presentValueSum(true)) * 100;
  };

  const exitMultipleSum = (cashFlows: number[]): number => {
    const cashFlow = cashFlows[cashFlows.length - 1] || 0;
    return cashFlows?.length ? cashFlow * form.exitMultiple : 0;
  };

  const discountedPresentValue = (cashFlows: number[]): number => {
    const i = cashFlows.length - 1;
    return cashFlows?.length
      ? presentValue(exitMultipleSum(cashFlows), i, form.discountRate)
      : 0;
  };

  const presentValueSum = (addCash = false): number => {
    const sum = presentValueCashFlows
      .slice(1)
      .reduce((val, acc) => val + acc, 0);
    const dpv = discountedPresentValue(cashFlows);
    return sum + dpv + (addCash ? form.cash : 0);
  };

  // createRows
  useMemo(() => {
    const rows = totalYears.map((i) => {
      const cf = cashFlows[i];
      const pvcf = presentValueCashFlows[i];
      return createData(i, fiscalYear(i), cf, pvcf);
    });

    setRows(rows);
  }, [cashFlows, presentValueCashFlows]);

  return (
    <div className="container">
      <Typography variant="h4" gutterBottom component="div">
        Intrinsic Value {form.ticker.length > 0 && ` for ${form.ticker}`}
      </Typography>
      <CalculatorInputForm
        form={form}
        onFormChange={handleInputChange}
      ></CalculatorInputForm>
      <CalculatorTable
        rows={rows}
        discount={calculateDiscount()}
        exitMultipleSum={exitMultipleSum(cashFlows)}
        discountedPresentValue={discountedPresentValue(cashFlows)}
        presentValueSum={presentValueSum()}
        presentValueSumCash={presentValueSum(true)}
        cash={form.cash}
        valuePerShare={calculateValuePerShare()}
        intrinsicValuePerShare={calculateIntrinsicValuePerShare()}
      ></CalculatorTable>
      <DonateLink></DonateLink>
    </div>
  );
}
